import { useState } from 'react'
import { FormControl, FormLabel, Input, Button, HStack, IconButton } from '@chakra-ui/react'
import { RefreshCw, Key } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'

interface PasswordGeneratorProps {
  value?: string
  onChange: (password: string) => void
  label?: string
  mb?: number
}

export function PasswordGenerator({ value = '', onChange, label = 'New Password', mb }: PasswordGeneratorProps) {
  const colors = useThemeColors()
  const [showPassword, setShowPassword] = useState(false)
  const [password, setPassword] = useState(value)

  // Generate a random password
  const generatePassword = (len: number = 15) => {
    const string = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ"
    const numeric = '0123456789'
    const punctuation = '@$!%*#?&'
    let generated = ""
    
    while (generated.length < len) {
      const char1 = string.charAt(Math.floor(Math.random() * string.length))
      const char2 = numeric.charAt(Math.floor(Math.random() * numeric.length))
      const char3 = punctuation.charAt(Math.floor(Math.random() * punctuation.length))
      generated += char1 + char2 + char3
    }

    // Shuffle and return the generated password
    const shuffled = generated.split('').sort(() => 0.5 - Math.random()).join('')
    const finalPassword = shuffled.substring(0, len)
    setPassword(finalPassword)
    onChange(finalPassword)
  }

  const handleShowPassword = () => {
    setShowPassword(true)
    generatePassword(15)
  }

  const handleHidePassword = () => {
    setShowPassword(false)
    setPassword('')
    onChange('')
  }

  const handleRegenerate = () => {
    generatePassword(15)
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value
    setPassword(newPassword)
    onChange(newPassword)
  }

  return (
    <FormControl mb={mb}>
      <FormLabel fontSize="sm" fontWeight="medium" color={colors.heading} mb={1}>
        {label}
      </FormLabel>
      {!showPassword ? (
        <Button
          leftIcon={<Key size={14} />}
          size="md"
          variant="outline"
          colorScheme="gray"
          onClick={handleShowPassword}
          borderRadius="md"
        >
          Generate Password
        </Button>
      ) : (
        <HStack spacing={2}>
          <Input
            type="text"
            value={password}
            onChange={handlePasswordChange}
            placeholder="Generated password"
            borderRadius="md"
            flex={1}
          />
          <IconButton
            aria-label="Regenerate password"
            icon={<RefreshCw size={16} />}
            size="md"
            variant="outline"
            colorScheme="blue"
            onClick={handleRegenerate}
            borderRadius="md"
          />
          <Button
            size="md"
            variant="outline"
            colorScheme="gray"
            onClick={handleHidePassword}
            borderRadius="md"
          >
            Cancel
          </Button>
        </HStack>
      )}
    </FormControl>
  )
}

